import React from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { useQuery } from '@tanstack/react-query';
import { outcomesApi } from '../../api/services';
import { CVD_BLUE, CVD_ORANGE } from '../../utils/cvdPalette';

// Location HVA roll-up chart (sc-6057): Pluto's plot_inventory_with_hva_markers
// ported to the Edge Review page — the location's on-hand line in BOXES (raw
// beacon counts) against its box-converted max capacity, with vertical markers
// for HVA conversions and go-live. Matches Pluto's visual semantics (line +
// ceiling + distinct vertical markers + legend + "no snapshot data after
// go-live" empty state), not its pixel styling. Read-time endpoint — nothing
// is cached in the review payload, so revisions here stay frontend-only.

// Marker pair uses the shared CVD-safe scheme (see cvdPalette.ts): blue =
// digest-driven, orange = ad-hoc — not Pluto's green/red. Line and go-live
// use neutrals so blue/orange stay reserved for the order semantics.
const INVENTORY = '#555'; // on-hand line — GroupDrawdownChart's stock gray
const MAX_CAP = '#333'; // ceiling, dashed — same idiom as GroupDrawdownChart's max
const HVA = CVD_BLUE; // digest-converted orders
const NON_HVA = CVD_ORANGE; // qualifying orders with no digest conversion
const GO_LIVE = '#000'; // go-live epoch, dashed black (Pluto's CVD variant)
const CHART_H = 260;

const toDate = (iso: string): Date => new Date(`${iso}T00:00:00Z`);

// Reference lines don't participate in the x-charts legend, so the marker
// legend is hand-rolled (the ticket's legend must cover the marker set).
const LegendSwatch: React.FC<{ color: string; dashed?: boolean; label: string }> = ({ color, dashed, label }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <Box sx={{ width: 18, height: 0, borderTop: `2px ${dashed ? 'dashed' : 'solid'} ${color}` }} />
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Stack>
);

export const LocationInventoryChart: React.FC<{ locationId: number }> = ({ locationId }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['outcomes', 'inventory-chart', locationId],
    queryFn: () => outcomesApi.inventoryChart(locationId),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  if (isError) {
    return (
      <Typography variant="body2" color="error" sx={{ p: 1 }}>
        Couldn’t load the inventory history — try again.
      </Typography>
    );
  }
  if (!data || data.since === null || data.series.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
        No inventory snapshot data after go-live.
      </Typography>
    );
  }

  const coveredSet = new Set(data.covered_dates);
  const stockMap = new Map(data.series.map((p) => [p.date, p.on_hand]));

  // x spans the full range (go-live → today) plus every covered/series/marker
  // day, so the reference markers land inside the axis domain even when the
  // last snapshot predates today.
  const days = Array.from(new Set([
    data.since,
    data.until,
    ...data.covered_dates,
    ...data.series.map((p) => p.date),
    ...data.hva_order_dates,
    ...data.non_hva_order_dates,
  ])).sort();
  const xDates = days.map(toDate);
  // Covered-but-empty day → genuine 0; a non-covered day → null so the line
  // bridges sensor gaps instead of dropping to a false floor (same rule as
  // GroupDrawdownChart).
  const stockData = days.map((d) => (stockMap.has(d) ? stockMap.get(d)! : (coveredSet.has(d) ? 0 : null)));
  // "If recs applied" ceiling (sc-6127): drawn only when it differs from the
  // current capacity — with nothing open the two coincide and one line tells
  // the story.
  const recCap = data.rec_capacity_boxes != null && data.rec_capacity_boxes !== data.max_capacity_boxes
    ? data.rec_capacity_boxes
    : null;

  // Pluto's headroom: the y ceiling sits above both the data peak and the max
  // line, so the capacity line renders inside the plot, not on its edge.
  const yMax = Math.ceil(Math.max(
    1,
    ...stockData.filter((v): v is number => v != null),
    data.max_capacity_boxes ?? 0,
    recCap ?? 0,
  ) * 1.05);

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 0.5 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <LegendSwatch color={INVENTORY} label="On hand (boxes, stocked groups)" />
        {data.max_capacity_boxes != null && <LegendSwatch color={MAX_CAP} dashed label="Current capacity (Σ max)" />}
        {recCap != null && <LegendSwatch color={HVA} dashed label="If recs applied (Σ recommended)" />}
        {data.account_linked && data.order_markers_available && (
          <>
            <LegendSwatch color={HVA} dashed label="HVA conversion" />
            <LegendSwatch color={NON_HVA} dashed label="Non-HVA order" />
          </>
        )}
        <LegendSwatch color={GO_LIVE} dashed label="Go-live" />
        {data.go_live_assumed && (
          <Typography variant="caption" color="text.secondary">
            assumed go-live (first snapshot): {data.since}
          </Typography>
        )}
        {!data.account_linked && (
          <Typography variant="caption" color="text.secondary">
            Order markers unavailable — no linked HQ account
          </Typography>
        )}
        {data.account_linked && !data.order_markers_available && (
          <Typography variant="caption" color="text.secondary">
            Order markers temporarily unavailable
          </Typography>
        )}
      </Stack>
      <Box sx={{ width: '100%', height: CHART_H }}>
        <LineChart
          height={CHART_H}
          xAxis={[{ scaleType: 'time', data: xDates, tickLabelStyle: { fontSize: 10 } }]}
          yAxis={[{ min: 0, max: yMax, tickLabelStyle: { fontSize: 10 } }]}
          series={[{
            // "stocked groups": the line covers only the chart universe
            // (groups with a max set) — not total physical inventory. Same
            // scope as Pluto's restockable filter; the label discloses it.
            id: 'stock', data: stockData, label: 'On hand (boxes, stocked groups)', color: INVENTORY,
            showMark: false, curve: 'stepAfter', connectNulls: true,
          }]}
          margin={{ top: 12, right: 16, bottom: 24, left: 40 }}
          slotProps={{ legend: { hidden: true } }}
        >
          {/* Non-HVA under HVA (Pluto's draw order): on a shared day the
              conversion marker wins visually — it's the headline signal.
              Both use the sibling chart's dashed order-event stroke. */}
          {data.non_hva_order_dates.map((d) => (
            <ChartsReferenceLine
              key={`non-hva-${d}`}
              x={toDate(d)}
              lineStyle={{ stroke: NON_HVA, strokeWidth: 1.5, strokeDasharray: '3 2', opacity: 0.75 }}
            />
          ))}
          {data.hva_order_dates.map((d) => (
            <ChartsReferenceLine
              key={`hva-${d}`}
              x={toDate(d)}
              lineStyle={{ stroke: HVA, strokeWidth: 1.5, strokeDasharray: '3 2', opacity: 0.85 }}
            />
          ))}
          <ChartsReferenceLine
            x={toDate(data.since)}
            lineStyle={{ stroke: GO_LIVE, strokeWidth: 1.5, strokeDasharray: '2 2' }}
          />
          {data.max_capacity_boxes != null && (
            <ChartsReferenceLine
              y={data.max_capacity_boxes}
              label={`max ${data.max_capacity_boxes}`}
              labelAlign="end"
              lineStyle={{ stroke: MAX_CAP, strokeWidth: 1, strokeDasharray: '6 3' }}
              labelStyle={{ fontSize: 10, fill: MAX_CAP }}
            />
          )}
          {/* If-recs ceiling (sc-6127) — the drawdown chart's rec-line grammar
              (CVD blue dashed) at location grain. */}
          {recCap != null && (
            <ChartsReferenceLine
              y={recCap}
              label={`if recs applied ${recCap}`}
              labelAlign="end"
              lineStyle={{ stroke: HVA, strokeWidth: 1.4, strokeDasharray: '8 4' }}
              labelStyle={{ fontSize: 10, fill: HVA, fontWeight: 600 }}
            />
          )}
        </LineChart>
      </Box>
    </Box>
  );
};
